import { FileCopyError, FILE_COPY_ERROR } from './fileCopy';
import { ImageConvertError, IMAGE_CONVERT_ERROR } from './imageConvert';

// Traduit une erreur d'upload (axios / API) en message affichable par l'utilisateur.
// Partagé par la publication d'un média (Publish) et l'upload d'avatar (AvatarUpload) :
// les deux passent par le même endpoint d'upload et renvoient donc les mêmes codes.
//
// `unsupportedTypeMessage` permet d'adapter le 415 au contexte : la publication accepte
// images ET vidéos, l'avatar uniquement des images.

// Échec de la copie locale (libs/fileCopy.js), tentée quand le téléphone a refusé d'envoyer le
// fichier d'origine. Ces cas affichaient « vérifiez votre connexion », alors que le réseau n'y
// est pour rien : on dit au membre ce qu'il peut réellement faire.
const FILE_COPY_MESSAGES = {
    [FILE_COPY_ERROR.NO_SPACE]: "Votre appareil n'a plus assez d'espace libre pour envoyer ce fichier. Libérez de la place (photos, vidéos ou applications inutilisées), puis réessayez.",
    [FILE_COPY_ERROR.TOO_LARGE]: "Ce fichier dépasse 1 Go, la taille maximale acceptée. Raccourcissez la vidéo ou choisissez-en une autre.",
    [FILE_COPY_ERROR.UNREADABLE]: "Votre appareil n'a pas laissé lire ce fichier. Patientez quelques secondes, sélectionnez-le à nouveau, puis réessayez.",
    [FILE_COPY_ERROR.VIDEO_UNAVAILABLE]: "Cette vidéo n'est plus disponible sur votre téléphone : elle est souvent stockée dans le cloud (Google Photos par exemple) pour libérer de la place. Téléchargez-la d'abord sur votre appareil, puis sélectionnez-la à nouveau.",
    [FILE_COPY_ERROR.UNKNOWN]: "Votre appareil n'a pas pu préparer ce fichier. Sélectionnez-le à nouveau, puis réessayez.",
};

// Envoi arrêté par le contrôle de coupure (useUploader) : plus aucun octet depuis 30 s.
export const UPLOAD_STALLED_CODE = 'ERR_UPLOAD_STALLED';
// Même arrêt, alors que le message « connexion lente » était affiché (useUploader).
export const UPLOAD_TOO_SLOW_CODE = 'ERR_UPLOAD_TOO_SLOW';

// Conversion HEIC/WEBP → JPEG (libs/imageConvert.js), faite à la sélection de la photo.
const IMAGE_CONVERT_MESSAGES = {
    [IMAGE_CONVERT_ERROR.ANIMATED]: "Les images animées ne sont pas acceptées. Choisissez une photo fixe ou une vidéo.",
    [IMAGE_CONVERT_ERROR.DECODE_FAILED]: "Cette photo n'a pas pu être préparée pour l'envoi. Réessayez, ou choisissez-la au format JPEG.",
};

export const getUploadErrorMessage = (err, { unsupportedTypeMessage } = {}) => {
    if (err instanceof ImageConvertError) {
        return IMAGE_CONVERT_MESSAGES[err.reason] || IMAGE_CONVERT_MESSAGES[IMAGE_CONVERT_ERROR.DECODE_FAILED];
    }
    if (err?.code === UPLOAD_TOO_SLOW_CODE) {
        return "L'envoi a échoué : votre connexion est trop lente ou instable. Réessayez avec une connexion plus stable, en wifi par exemple.";
    }
    if (err?.code === UPLOAD_STALLED_CODE) {
        return "L'envoi a échoué suite à une perte de connexion. Vérifiez votre réseau puis réessayez.";
    }
    if (err instanceof FileCopyError) {
        return FILE_COPY_MESSAGES[err.reason] || FILE_COPY_MESSAGES[FILE_COPY_ERROR.UNKNOWN];
    }

    const response = err?.response || {};
    const statusCode = response?.statusCode || response?.status;
    const backendMessage = response?.message
        || err?.message
        || err?.output?.payload?.message
        || err?.data?.message;

    if (statusCode === 413) {
        return "Le fichier est trop volumineux. Réduisez sa taille puis réessayez.";
    }
    if (statusCode === 415) {
        return unsupportedTypeMessage || "Type de fichier non pris en charge. Utilisez un format image/vidéo compatible.";
    }
    if (err?.code === "ERR_NETWORK" || err?.code === "ECONNABORTED") {
        return "Impossible d'envoyer le fichier pour le moment. Vérifiez votre connexion puis réessayez.";
    }
    if (backendMessage) {
        return backendMessage;
    }
    return "Envoi du fichier impossible. Veuillez réessayer.";
};

export default getUploadErrorMessage;
